import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchMyMembership, type MembershipPlan } from './membershipService';
import {
  applyServerEnrollmentToLocal,
  readMembershipStatus,
  type StoredMembershipStatus,
} from './membershipStatus';

function perkLabelsFromPlan(plan: MembershipPlan | undefined): string[] | undefined {
  if (!plan?.perks?.length) return undefined;
  return [...plan.perks]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((p) => p.label);
}

/**
 * Shared membership status for hub + app shell.
 * Local storage is optimistic; server `/membership/me` wins when available.
 */
export function useClientMembership(options?: {
  /** Optional catalog plans for richer server→local mapping */
  plans?: MembershipPlan[];
  /** When false, skip the server refresh (topbar can stay light) */
  syncFromServer?: boolean;
}) {
  const { client } = useAuth();
  const clientId = client?.client_id || null;
  const syncFromServer = options?.syncFromServer !== false;
  const plans = options?.plans;

  const [membership, setMembership] = useState<StoredMembershipStatus>(() =>
    readMembershipStatus(clientId)
  );

  const refresh = useCallback(() => {
    setMembership(readMembershipStatus(clientId));
  }, [clientId]);

  useEffect(() => {
    setMembership(readMembershipStatus(clientId));
  }, [clientId]);

  useEffect(() => {
    const onChange = () => setMembership(readMembershipStatus(clientId));
    window.addEventListener('storage', onChange);
    window.addEventListener('cwy-membership-status', onChange);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener('cwy-membership-status', onChange);
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId || !syncFromServer) return;
    let cancelled = false;
    (async () => {
      try {
        const mine = await fetchMyMembership();
        if (cancelled || !mine.enrollment || mine.status === 'none') return;
        const planMeta = plans?.find((p) => p.plan_id === mine.enrollment?.plan_id);
        applyServerEnrollmentToLocal(
          clientId,
          { status: mine.status, enrollment: mine.enrollment },
          planMeta
            ? {
                plan_name: planMeta.name,
                name: planMeta.name,
                subtitle: planMeta.subtitle,
                plan_subtitle: planMeta.subtitle,
                service_discount_percent: planMeta.service_discount_percent,
                perk_labels: perkLabelsFromPlan(planMeta),
              }
            : undefined
        );
        if (!cancelled) setMembership(readMembershipStatus(clientId));
      } catch {
        /* offline — keep local */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, syncFromServer, plans]);

  const isLive = membership.status === 'active';
  const isPending = membership.status === 'pending_checkout';
  const isExpired = membership.status === 'expired';

  return {
    clientId,
    membership,
    setMembership,
    refresh,
    isLive,
    isPending,
    isExpired,
  };
}

export function formatMembershipTierLabel(
  tier: 'bronze' | 'silver' | 'gold' | undefined
): string {
  if (!tier) return 'Member';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}
